"use client";

import { useState } from "react";
import { X, Loader2, Check } from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  type: string;
  showDropdown?: boolean;
}

export function WaitlistModal({ isOpen, onClose, title, description, type, showDropdown = false }: WaitlistModalProps) {
  const [email, setEmail] = useState("");
  const [watchType, setWatchType] = useState("Apple Watch");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError(null);

    const data: any = {
      email,
      type: type,
      message: `Request to join ${title} waitlist`,
      timestamp: serverTimestamp(),
      source: "get_started_page"
    };

    if (showDropdown) {
      data.watchType = watchType;
      data.message = `Request to join ${title} waitlist for ${watchType}`;
    }

    try {
      await addDoc(collection(db, "dbContact"), data);
      setSuccess(true);
      setEmail("");
    } catch (err) {
      console.error("Error adding document: ", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl w-full max-w-md p-6 shadow-2xl relative animate-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#8E9285] hover:text-[#E2E3D8] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-sriracha text-[#E2E3D8] mb-2">
            {title}
          </h2>
          <p className="text-[#C4C8BA] font-geist text-sm">
            {description}
          </p>
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-[#344E17]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-[#B0D18B]" />
            </div>
            <p className="text-[#E2E3D8] font-medium mb-4">You&apos;re on the list!</p>
            <button
              onClick={onClose}
              className="text-[#B0D18B] hover:text-[#9DC077] text-sm font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {showDropdown && (
              <div>
                <select
                  value={watchType}
                  onChange={(e) => setWatchType(e.target.value)}
                  className="w-full bg-[#12140E] border border-[#8E9285]/20 rounded-lg px-4 py-3 text-[#E2E3D8] focus:outline-none focus:border-[#B0D18B] transition-colors font-geist appearance-none cursor-pointer"
                >
                  <option value="Apple Watch">Apple Watch</option>
                  <option value="Galaxy Watch">Galaxy Watch</option>
                  <option value="Google Pixel Watch">Google Pixel Watch</option>
                  <option value="Fitbit Watch">Fitbit Watch</option>
                </select>
              </div>
            )}
            <div>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#12140E] border border-[#8E9285]/20 rounded-lg px-4 py-3 text-[#E2E3D8] placeholder-[#5C5F56] focus:outline-none focus:border-[#B0D18B] transition-colors font-geist"
              />
            </div>

            {error && (
              <p className="text-red-400 text-xs text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-geist"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Notify Me"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
